import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from 'src/global';
import { RootState } from 'src/store';
import { collectionsSelectors, updateCollection } from './collections.state';
import { Collection } from './collections.types';
import { ContentCard, ContentCardLocale } from 'src/features/contents/contents.types';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { uploadCollectionImage } from 'src/features/collections/collections.api';

export const useCollectionsState = (): RootState['collections'] => {
  const collections = useSelector<RootState, RootState['collections']>(state => state.collections);

  useDebugValue(collections);

  return collections;
};

export const useCollections = (): Collection[] => {
  const collections = useSelector<RootState, RootState['collections']>(state => state.collections);

  const result = collectionsSelectors.selectAll(collections);

  useDebugValue(result);

  return result;
};

export const useCollectionById = (id: string): AsyncEntity<Collection> | null => {
  const collections = useSelector<RootState, RootState['collections']>(state => state.collections);

  const result = collectionsSelectors.selectById(collections, id) || null;

  useDebugValue(result);

  return result;
};

export const useCollectionCardsById = (id: string): AsyncEntity<ContentCard[]> => {
  const collections = useSelector<RootState, RootState['collections']>(state => state.collections);

  const result = collectionsSelectors.selectById(collections, id) as Collection;

  useDebugValue(result.cards);

  return result.cards;
};

export type UseCollectionPoster = (params: { collectionId: string }) => {
  progress: number;
  uploadContentImage: (file: File) => void;
};

export const useCollectionImage: UseCollectionPoster = ({ collectionId }) => {
  const dispatch: AppDispatch = useDispatch();
  const [progress, uploadFile] = useUploadFileToBucket();

  const uploadContentImage = useCallback(
    async (file: File) => {
      const { name: filename } = file;

      const credentials = await uploadCollectionImage({
        collectionId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });

        if (response) {
          dispatch(
            updateCollection({
              collectionId,
              data: { image: `${url}/${key}` },
            })
          );
        }
      }
    },
    [collectionId, dispatch, uploadFile]
  );

  return { progress, uploadContentImage };
};

export const useCollectionLocalesById = (id: string): AsyncEntity<ContentCardLocale[]> => {
  const contents = useSelector<RootState, RootState['collections']>(state => state.collections);

  const result = collectionsSelectors.selectById(contents, id) || null;

  useDebugValue(result);

  return result?.locales || [];
};
